import './index.css'

import React, { useEffect, useState } from 'react'

import { COLOR_INDICATORS } from '@disco/constants/FeatureTypes'
import { sendMixpanelEvent } from '@disco/state-management/actions/AnalyticsActions'
import {
  deleteCustomNativeTag,
  editCustomNativeTag,
  shareCustomNativeTag,
} from '@disco/state-management/actions/CustomNativeTagActions'
import { isDeviceTypePhone } from '@disco/state-management/selectors/app'
import type { featureData } from '@disco/state-management/selectors/business'
import { useFeatureData } from '@disco/state-management/selectors/business'
import { getNativeTags } from '@disco/state-management/selectors/tags'

import type { State } from 'types/redux'
import type { NativeTagWithCategory } from 'types/track'
import type { PopoverItemType } from 'types/ui'

import ColorIndicatorsAlert from 'components/common/color-indicators-alert'
import DocumentHeadTags from 'components/document-head-tags'
import { Flex } from 'components/ds'
import Button from 'components/ui/button'
import DataTable from 'components/ui/data-table'
import EllipsisMenu from 'components/ui/ellipsis-menu'
import Icon from 'components/ui/icon'
import InlineLink from 'components/ui/inline-link'
import Input from 'components/ui/input'
import ModalDialog from 'components/ui/modal-dialog'
import PageContainer from 'components/ui/page-container'
import PageHeader from 'components/ui/page-header'
import PricingBadge from 'components/ui/pricing-badge'
import Switch from 'components/ui/switch'
import Tag from 'components/ui/tag'
import Tooltip from 'components/ui/tooltip'

import { useDispatch, useSelector } from 'hooks/use-redux'

import TrackTagColorIndicatorModal from './TrackTagColorIndicatorModal'
import TrackTagEditModal from './TrackTagEditModal'

const TrackTagsSettings = (): React.ReactNode => {
  const dispatch = useDispatch()
  const nativeTags: NativeTagWithCategory[] = useSelector(getNativeTags)
  const isPhone = useSelector(isDeviceTypePhone)
  const isNativeTagsReadonly: boolean = useSelector(
    (state: State) =>
      state.user.business?.private_metadata.is_native_tags_readonly,
  )
  const {
    isTrial,
    badgeTitle: colorIndicatorsBadgeTitle,
    isFeatureEnabled: colorIndicatorsEnabled,
  }: featureData = useFeatureData(COLOR_INDICATORS)
  const [modal, setModal] = useState<React.ReactNode>(null)

  const closeModal = (): void => setModal(null)

  const [query, setQuery] = useState<string>('')

  const updateSearchQuery = (event: React.ChangeEvent<HTMLInputElement>) =>
    setQuery(event.target.value)

  useEffect(() => {
    dispatch(sendMixpanelEvent('Open track tags settings'))
  }, [])
  const shareSettingsTooltip: string = isTrial
    ? 'Subscribe to Pro to control the share settings on your tags.'
    : 'Upgrade to control the share settings on your tags.'

  const getPricingBadge = (): React.ReactNode | null | undefined =>
    !colorIndicatorsEnabled ? (
      <PricingBadge
        title={colorIndicatorsBadgeTitle}
        intercomMsg="I’m interested in subscribing to Pro to access tag color indicators"
      />
    ) : undefined

  const getMenuOptions = (tag: NativeTagWithCategory): PopoverItemType[] => {
    let options = []

    if (!tag.is_default_tag) {
      options = [
        ...options,
        {
          label: 'Edit',
          onClick: () =>
            setModal(
              <TrackTagEditModal
                editingTag={tag}
                onRequestClose={closeModal}
              />,
            ),
        },
      ]
    }

    if (tag.color) {
      options = [
        ...options,
        {
          badge: getPricingBadge(),
          disabled: !colorIndicatorsEnabled,
          label: 'Remove color indicator',
          onClick: () =>
            dispatch(
              editCustomNativeTag(tag.id, {
                color: null,
              }),
            ),
        },
      ]
    } else {
      options = [
        ...options,
        {
          badge: getPricingBadge(),
          disabled: !colorIndicatorsEnabled,
          label: 'Add color indicator',
          onClick: () =>
            setModal(
              <TrackTagColorIndicatorModal
                tag={tag}
                onClose={closeModal}
                onSubmit={async color => {
                  try {
                    await dispatch(
                      editCustomNativeTag(tag.id, {
                        color,
                      }),
                    )
                    closeModal()
                  } catch (error) {}
                }}
              />,
            ),
        },
      ]
    }

    if (!tag.is_default_tag) {
      options = [
        ...options,
        {
          label: 'Delete',
          destructive: true,
          onClick: () =>
            setModal(
              <ModalDialog
                title="Delete track tag"
                primaryAction={{
                  caption: 'Delete',
                  onClick: async () => {
                    try {
                      await dispatch(deleteCustomNativeTag(tag))
                      closeModal()
                    } catch (error) {}
                  },
                }}
                status="danger"
                onCancel={closeModal}
              >
                Are you sure you want to delete “{tag.name}”? This can’t be
                undone.
              </ModalDialog>,
            ),
        },
      ]
    }

    return options
  }

  return (
    <>
      <DocumentHeadTags title="Settings | Track Tags | DISCO" />
      <PageHeader title="Manage Track Tags" />
      <PageContainer>
        <Flex gap="2" direction={isPhone ? 'column' : 'row'} pb="2">
          <div className="TrackTagsSettings_SearchInput">
            <Input
              icon="search"
              placeholder="Search"
              value={query}
              onChange={updateSearchQuery}
            />
          </div>
          <div>
            <Button
              caption="Add track tags"
              disabled={isNativeTagsReadonly}
              icon="add-tag"
              tooltip={
                isNativeTagsReadonly
                  ? 'Tag editing is not enabled for this DISCO.'
                  : undefined
              }
              onClick={() =>
                setModal(<TrackTagEditModal onRequestClose={closeModal} />)
              }
            />
          </div>
        </Flex>
        {!isNativeTagsReadonly && <ColorIndicatorsAlert />}
        <DataTable
          appearance="plain"
          columns={[
            {
              name: 'Name',
              toSortable: (tag: Record<string, any>) => tag.name,
              render: (tag: Record<string, any>) => (
                <Tag
                  tag={tag.name}
                  tagColor={colorIndicatorsEnabled ? tag.color : undefined}
                />
              ),
            },
            {
              name: 'Category',
              toSortable: (tag: Record<string, any>) => tag.category.name,
              render: (tag: Record<string, any>) => tag.category.name,
            },
            {
              name: 'Type',
              toSortable: (tag: Record<string, any>) =>
                tag.is_default_tag ? 'Default' : 'Custom',
              render: (tag: Record<string, any>) =>
                tag.is_default_tag ? 'Default' : 'Custom',
            },
            {
              name: 'Share settings',
              contentType: 'button',
              width: 'content',
              headerRender: (name: string) => (
                <div className="flc">
                  {name}
                  {' '}
                  <Tooltip
                    content={
                      <>
                        Tag share settings let you make tags private, which
                        means they won’t download in the metadata on your
                        tracks, or appear in Catalogs.{' '}
                        <InlineLink
                          to="https://support.disco.ac/home/marking-your-track-tags-as-private"
                          rel="noopener noreferrer"
                          target="_blank"
                        >
                          Find out more.
                        </InlineLink>
                      </>
                    }
                  >
                    <Icon type="info" width="16" height="16" />
                  </Tooltip>
                </div>
              ),
              render: (tag: NativeTagWithCategory) => (
                <Tooltip
                  content={
                    !colorIndicatorsEnabled ? shareSettingsTooltip : undefined
                  }
                >
                  <Switch
                    disabled={!colorIndicatorsEnabled}
                    checkedIcon="lock-open"
                    uncheckedIcon="lock"
                    value={tag.tag_is_shared}
                    onChange={(value: boolean) => {
                      dispatch(shareCustomNativeTag(tag.id, value))
                    }}
                  />
                </Tooltip>
              ),
            },
            {
              contentType: 'button',
              name: 'Actions',
              width: 'content',
              render: (tag: NativeTagWithCategory) => (
                <EllipsisMenu
                  compact
                  disabled={isNativeTagsReadonly}
                  hasArrow={false}
                  icon="ellipsis"
                  options={getMenuOptions(tag)}
                />
              ),
            },
          ]}
          defaultSort={{
            col: 1,
            dir: 1,
          }}
          data={nativeTags
            .map((tag: NativeTagWithCategory) => ({ ...tag, key: tag.id }))
            .filter(
              // TODO: unify this with other inline search
              (tag: NativeTagWithCategory) =>
                !query ||
                tag.name.toLowerCase().includes(query.toLowerCase()) ||
                tag.category.name.toLowerCase().includes(query.toLowerCase()),
            )}
          emptyState={
            query
              ? 'Sorry! That tag doesn’t exist. Create a new track tag above.'
              : 'No track tags here yet!'
          }
        />
        {modal}
      </PageContainer>
    </>
  )
}

export default TrackTagsSettings
